import React, { useState } from 'react';
import {
  useErrorById,
  useResolveError,
  useUnresolveError,
  useDeleteError,
} from '@/hooks/useAdminErrors';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  AlertCircle,
  CheckCircle,
  Trash2,
  User,
  Globe,
  Monitor,
  Calendar,
  Hash,
  FileText,
  Settings,
} from 'lucide-react';
import { format } from 'date-fns';

interface ErrorDetailsModalProps {
  errorId: string;
  onClose: () => void;
}

export const ErrorDetailsModal: React.FC<ErrorDetailsModalProps> = ({
  errorId,
  onClose,
}) => {
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolutionInput, setShowResolutionInput] = useState(false);

  const { data: error, isLoading } = useErrorById(errorId);
  const resolveMutation = useResolveError();
  const unresolveMutation = useUnresolveError();
  const deleteMutation = useDeleteError();

  const handleResolve = async () => {
    await resolveMutation.mutateAsync({
      errorId,
      resolution_notes: resolutionNotes || undefined,
    });
    setResolutionNotes('');
    setShowResolutionInput(false);
    onClose();
  };

  const handleUnresolve = async () => {
    await unresolveMutation.mutateAsync(errorId);
    onClose();
  };

  const handleDelete = async () => {
    if (confirm('Are you sure you want to delete this error log?')) {
      await deleteMutation.mutateAsync(errorId);
      onClose();
    }
  };

  if (isLoading) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading error details...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!error) {
    return null;
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Error Details
          </DialogTitle>
          <DialogDescription>
            {error.error_code || 'Error Log'} •{' '}
            {format(new Date(error.created_at), 'PPpp')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status and Actions */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Badge
                variant={
                  error.severity === 'critical' || error.severity === 'error'
                    ? 'destructive'
                    : 'default'
                }
              >
                {error.severity.toUpperCase()}
              </Badge>
              <Badge variant="outline">{error.error_type}</Badge>
              {error.resolved ? (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Resolved
                </Badge>
              ) : (
                <Badge variant="outline">Open</Badge>
              )}
            </div>
            <div className="flex gap-2">
              {!error.resolved ? (
                <Button
                  size="sm"
                  onClick={() => setShowResolutionInput(!showResolutionInput)}
                  variant="outline"
                >
                  Mark Resolved
                </Button>
              ) : (
                <Button size="sm" onClick={handleUnresolve} variant="outline">
                  Reopen
                </Button>
              )}
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Resolution Input */}
          {showResolutionInput && !error.resolved && (
            <div className="space-y-2 p-4 bg-muted rounded-lg">
              <label className="text-sm font-medium">Resolution Notes (Optional)</label>
              <Textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Add notes about how this error was resolved..."
                rows={3}
              />
              <div className="flex gap-2 justify-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowResolutionInput(false);
                    setResolutionNotes('');
                  }}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={handleResolve}>
                  Confirm Resolution
                </Button>
              </div>
            </div>
          )}

          {/* Resolution Info */}
          {error.resolved && error.resolution_notes && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-green-900 dark:text-green-100 mb-1">
                    Resolution Notes
                  </h4>
                  <p className="text-sm text-green-800 dark:text-green-200">
                    {error.resolution_notes}
                  </p>
                  {error.resolved_at && (
                    <p className="text-xs text-green-700 dark:text-green-300 mt-2">
                      Resolved {format(new Date(error.resolved_at), 'PPpp')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          <div className="space-y-2">
            <h3 className="font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Error Message
            </h3>
            <div className="p-4 bg-muted rounded-lg font-mono text-sm">
              {error.message}
            </div>
          </div>

          {/* Stack Trace */}
          {error.stack_trace && (
            <div className="space-y-2">
              <h3 className="font-semibold">Stack Trace</h3>
              <pre className="p-4 bg-muted rounded-lg text-xs overflow-x-auto">
                {error.stack_trace}
              </pre>
            </div>
          )}

          <Separator />

          {/* Metadata Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* User Info */}
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <User className="h-4 w-4" />
                User Information
              </h3>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email:</span>
                  <span>{error.user_email || 'Anonymous'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">User ID:</span>
                  <span className="font-mono text-xs">
                    {error.user_id || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">IP Address:</span>
                  <span>{error.ip_address || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Request Info */}
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Request Information
              </h3>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Path:</span>
                  <span className="font-mono text-xs">
                    {error.request_path || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Method:</span>
                  <span>{error.request_method || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Environment Info */}
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Monitor className="h-4 w-4" />
                Environment
              </h3>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Browser:</span>
                  <span>{error.browser || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">OS:</span>
                  <span>{error.os || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">User Agent:</span>
                  <span className="text-xs truncate max-w-[200px]" title={error.user_agent || ''}>
                    {error.user_agent || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Occurrence Info */}
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Hash className="h-4 w-4" />
                Occurrence
              </h3>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Count:</span>
                  <Badge variant="outline">{error.occurrence_count}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">First Seen:</span>
                  <span>{format(new Date(error.first_seen), 'PPp')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Seen:</span>
                  <span>{format(new Date(error.last_seen), 'PPp')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Metadata */}
          {error.metadata && Object.keys(error.metadata).length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Additional Metadata
              </h3>
              <pre className="p-4 bg-muted rounded-lg text-xs overflow-x-auto">
                {JSON.stringify(error.metadata, null, 2)}
              </pre>
            </div>
          )}

          {/* Environment Data */}
          {error.environment && Object.keys(error.environment).length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold">Environment Data</h3>
              <pre className="p-4 bg-muted rounded-lg text-xs overflow-x-auto">
                {JSON.stringify(error.environment, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
